import React, { FC, useRef } from 'react'
import { LocalizationType, SignupProps } from '../../types'
import { SignupProvider } from './signupProvider'
import { FlowManagerRoutes } from '../../flows/shared/FlowManagerRoutes'
import { AbTesting } from '../../flows/shared/AbTesting'
import { SessionData } from '../../lib/sessionData'

/**
 * Creates a localization override from onboarding session app locale
 * @param originalLocalization - The original localization object
 * @param onboardingLocale - The locale from onboarding session app (format: "language-country" or "language_country")
 * @returns Overridden localization object or original if no valid locale found
 */
function createLocalizationOverride(
  originalLocalization: LocalizationType,
  onboardingLocale?: string
) {
  if (!onboardingLocale) {
    return originalLocalization
  }

  // Parse locale format: "language-country" or "language_country" (e.g., "en-US", "en_US", "fr-CA", "fr_CA")
  const localeParts = onboardingLocale.split(/[-_]/)
  if (localeParts.length !== 2) {
    return originalLocalization
  }

  const [language, country] = localeParts

  // Override the localization with locale from onboarding session
  return {
    ...originalLocalization,
    locale: onboardingLocale,
    language: language.toLowerCase(),
    country: country.toLowerCase()
  }
}

export const Signup: FC<SignupProps> = (props) => {
  const sessionData = useRef(SessionData.getSessionData(props.properties?.flow))
    .current

  // Extract locale from onboarding session app and override localization
  const overriddenLocalization = createLocalizationOverride(
    props.localization,
    props.onboarding?.sessionContext?.app?.locale
  )

  // Create props with overridden localization
  const propsWithOverriddenLocalization = {
    ...props,
    localization: overriddenLocalization
  }

  return (
    <SignupProvider
      signupProps={propsWithOverriddenLocalization}
      sessionData={sessionData}
    >
      <AbTesting />
      <FlowManagerRoutes />
    </SignupProvider>
  )
}
